import React, { useEffect, useState } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormControlLabel,
  Switch,
  TextField,
  ImageList,
  ImageListItem,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Close as CloseIcon, Visibility as VisibilityIcon, Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import { reportsApi, postsApi } from '../services/api';
import { Link, useNavigate } from 'react-router-dom';

const statusColor = (status) => {
  switch (status) {
    case 'SUBMITTED':
      return 'warning';
    case 'CLOSED':
      return 'success';
    default:
      return 'default';
  }
};

const Reports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(true);

  // Post view dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [adminPost, setAdminPost] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [visibleToggle, setVisibleToggle] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchReports = async (pageNum = page, pageSize = rowsPerPage, openOnly = onlyOpen) => {
    setLoading(true);
    setError('');
    try {
      const status = openOnly ? 'SUBMITTED' : null;
      const data = await reportsApi.getAllReportsPaginated(pageNum, pageSize, status);
      setReports(data?.content || []);
      setTotalElements(data?.totalElements || 0);
    } catch (e) {
      console.error('Failed to fetch reports', e);
      setError('Failed to load reports. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, onlyOpen]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleOnlyOpen = (event) => {
    setOnlyOpen(event.target.checked);
    setPage(0);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setSelectedReport(null);
  };

  const handleCloseReport = async (reportId) => {
    try {
      await reportsApi.closeReport(reportId);
      await fetchReports();
    } catch (e) {
      console.error('Failed to close report', e);
      setError('Failed to close report. Please try again.');
    }
  };

  return (
    <>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1">Reports</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={<Switch checked={onlyOpen} onChange={handleToggleOnlyOpen} color="primary" />}
            label="Show only open reports"
          />
          <Tooltip title="Refresh">
            <IconButton color="primary" onClick={() => fetchReports()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Reporting User</TableCell>
                <TableCell>Post</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No reports found</TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow key={report.id} hover>
                    <TableCell>{report.id}</TableCell>
                    <TableCell>
                      {report.reportingUser?.name || report.reportingUser?.email || report.reportingUser?.id}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Link to={`/posts/${report.post?.id}`} style={{ textDecoration: 'none' }}>
                          <Typography variant="body2">Post #{report.post?.id}</Typography>
                        </Link>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" noWrap title={report.reason}>{report.reason}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={report.status} color={statusColor(report.status)} size="small" />
                    </TableCell>
                    <TableCell>
                      {new Date(report.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {report.status === 'SUBMITTED' && (
                        <Tooltip title="Close report">
                          <span>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CloseIcon />}
                              onClick={() => handleCloseReport(report.id)}
                            >
                              Close
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      {/* View Post Dialog */}
      <Dialog open={viewOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle>Post Details</DialogTitle>
        <DialogContent dividers>
          {!adminPost ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Post #{adminPost.id}
              </Typography>

              <TextField
                label="Text"
                fullWidth
                multiline
                minRows={3}
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                sx={{ mt: 1, mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={visibleToggle}
                    onChange={(e) => setVisibleToggle(e.target.checked)}
                    color="primary"
                  />
                }
                label={visibleToggle ? 'Visible' : 'Hidden'}
              />

              {Array.isArray(adminPost.media) && adminPost.media.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Images</Typography>
                  <ImageList cols={3} gap={8}>
                    {adminPost.media.map((m, idx) => (
                      <ImageListItem key={idx}>
                        <img src={m.mediumUrl || m.largeUrl || m.smallUrl} alt={m.name || `media-${idx}`} loading="lazy" />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseView} disabled={saving}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={!adminPost || saving}
            onClick={async () => {
              if (!adminPost) return;
              setSaving(true);
              setError('');
              try {
                // Update visibility
                await postsApi.adminSetVisibility(adminPost.id, visibleToggle);
                await fetchReports();
                setViewOpen(false);
                setAdminPost(null);
              } catch (e) {
                console.error('Failed to save post changes', e);
                setError('Failed to save changes.');
              } finally {
                setSaving(false);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Reports;
